import { usePricingContext } from '@vp/react-pricing-context';
import { MonitorPlay } from 'lucide-react';
import { GridContainer, H3, Icon, Row, SelectionSet } from '@vp/swan';
import { Currency } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { ProductOptionsPricingQuery, ProductOptionsQuery } from '../../../graphql';
import { OptionInput } from './OptionInput';

export type ProductOptionsProps = {
    productOptions: ProductOptionsQuery['productOptions'];
    selectedProductOptions: Record<string, string>;
    setSelectedProductOptions: (options: Record<string, string>) => void;
    pivotPricing: ProductOptionsPricingQuery['selectionsPricing']['pivotPrices'];
    currency: Currency;
    loading: boolean;
};

export function ProductOptions({
    productOptions,
    selectedProductOptions,
    setSelectedProductOptions,
    pivotPricing,
    currency,
    loading,
}: ProductOptionsProps) {
    const pricingContext = usePricingContext();

    const optionMap = new Map<string, { price: number; isCompatible: boolean }>();
    pivotPricing?.forEach((pivot) => {
        optionMap.set(`${pivot.key}:${pivot.value}`, {
            price: pricingContext?.vatInclusive
                ? pivot.differentialPrice.taxed
                : pivot.differentialPrice.untaxed,
            isCompatible: pivot.isCompatible,
        });
    });

    const handleOnChange = (optionKey: string, valueKey: string) => {
        const newSelectedOptions = { ...selectedProductOptions };
        newSelectedOptions[optionKey] = valueKey;
        setSelectedProductOptions(newSelectedOptions);
    };

    const price = (optionKey: string, valueKey: string) => {
        return optionMap.get(`${optionKey}:${valueKey}`)?.price;
    };

    const isDisabled = (optionKey: string, valueKey: string) => {
        return (
            !optionMap.get(`${optionKey}:${valueKey}`)?.isCompatible ||
            price(optionKey, valueKey) === undefined
        );
    };

    const optionInputIcon = (optionValueKey: string): JSX.Element | undefined => {
        switch (optionValueKey) {
            case 'Sync':
                return <MonitorPlay />;
            case 'Async':
                return <Icon iconType="email" />;
            default:
                return undefined;
        }
    };

    return productOptions.map((option, idx) => (
        <div key={option.key.key}>
            <H3 fontSkin="title-subsection" id={`product-option-${idx}`}>
                {option.key.merchandisingName}
            </H3>
            <SelectionSet
                variant="single-select"
                mb={4}
                defaultSelectedValue={selectedProductOptions[option.key.key]}
                onChange={({ target }) =>
                    handleOnChange(option.key.key, (target as HTMLInputElement).value)
                }
            >
                <GridContainer>
                    <Row marginTop={5}>
                        {option.values.map((value) => {
                            return (
                                <OptionInput
                                    key={value.key}
                                    title={value.merchandisingName}
                                    value={value.key}
                                    currency={currency}
                                    loading={loading}
                                    disabled={isDisabled(option.key.key, value.key)}
                                    price={price(option.key.key, value.key) || 0}
                                    icon={optionInputIcon(value.key)}
                                />
                            );
                        })}
                    </Row>
                </GridContainer>
            </SelectionSet>
        </div>
    ));
}
